import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Leaf, Facebook, Instagram, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-forest-950 text-white" data-testid="footer">
      {/* CTA Section */}
      <div className="border-b border-emerald-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
              Ready to Transform Your Lawn?
            </h2>
            <p className="text-emerald-100/80 text-lg mb-8">
              Get a free, no-obligation quote today and discover why Houston homeowners trust Nexivas for their lawn care needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="footer-get-quote-btn"
                >
                  Get Your Free Quote
                </Button>
              </Link>
              <a href="tel:+1-771-1871-987">
                <Button 
                  variant="outline" 
                  className="border-2 border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-white rounded-full px-8 py-6 text-lg transition-all duration-300"
                  data-testid="footer-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-bold">Nexivas</span>
            </Link>
            <p className="text-emerald-100/70 mb-6">
              Professional lawn care services for a greener, healthier yard. Serving Houston and surrounding areas with excellence.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                className="w-10 h-10 bg-emerald-900 hover:bg-emerald-800 rounded-full flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-emerald-900 hover:bg-emerald-800 rounded-full flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-emerald-900 hover:bg-emerald-800 rounded-full flex items-center justify-center transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="footer-link" data-testid="footer-home-link">Home</Link>
              </li>
              <li>
                <Link to="/services" className="footer-link" data-testid="footer-services-link">Services</Link>
              </li>
              <li>
                <Link to="/about" className="footer-link" data-testid="footer-about-link">About Us</Link>
              </li>
              <li>
                <Link to="/service-areas" className="footer-link" data-testid="footer-areas-link">Service Areas</Link>
              </li>
              <li>
                <Link to="/contact" className="footer-link" data-testid="footer-contact-link">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-6">Our Services</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/services" className="footer-link">Lawn Mowing</Link>
              </li>
              <li>
                <Link to="/services" className="footer-link">Lawn Maintenance</Link>
              </li>
              <li>
                <Link to="/services" className="footer-link">Weed Control</Link>
              </li>
              <li>
                <Link to="/services" className="footer-link">Fertilization</Link>
              </li>
              <li>
                <Link to="/services" className="footer-link">Yard Cleanup</Link>
              </li>
              <li>
                <Link to="/services" className="footer-link">Landscaping</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-emerald-100/70 text-sm">Call Us</p>
                  <a href="tel:+1-771-1871-987" className="hover:text-emerald-400 transition-colors" data-testid="footer-phone">
                    +1-771-1871-987
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-emerald-100/70 text-sm">Email Us</p>
                  <a href="mailto:support@nexivas.store" className="hover:text-emerald-400 transition-colors" data-testid="footer-email">
                    support@nexivas.store
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-emerald-100/70 text-sm">Address</p>
                  <p data-testid="footer-address">667 Magnolia Court<br />Houston, TX 57002</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-emerald-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-emerald-100/60 text-sm text-center md:text-left">
              &copy; {currentYear} Nexivas Lawn Care. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-emerald-100/60 hover:text-white text-sm transition-colors" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-emerald-100/60 hover:text-white text-sm transition-colors" data-testid="footer-terms-link">
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
